import { useState } from 'react';
import { useLingui } from '@lingui/react';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { useCurrency } from '@/hooks/useCurrency';
import { Separator } from '@/components/ui/separator';
import { useDunningWorkflowDetail } from '@/hooks/billing/useDunningWorkflows';
import { DunningWorkflowHeader } from './DunningWorkflowHeader';
import { DunningOpenInvoicesTable } from './DunningOpenInvoicesTable';
import { DunningWorkflowTimeline } from './DunningWorkflowTimeline';
import { DunningManualActions } from './DunningManualActions';
import type { DunningManualActionKind } from './DunningManualActions';
import { DunningSettingsSnapshot } from './DunningSettingsSnapshot';
import { PauseModal } from './PauseModal';
import { ConfirmWithReasonModal } from './ConfirmWithReasonModal';
import {
  usePauseDunning,
  useResumeDunning,
  useSendNowDunning,
  useHandoverDunning,
  useCancelDunning,
  useRetryDunningSend,
} from '@/hooks/billing/useDunningManualActions';
import { DunningBlockerBanner } from './DunningBlockerBanner';
import { useNavigate } from 'react-router';

interface Props {
  workflowItemId: string | null;
  onClose: () => void;
}

type ActiveModal = DunningManualActionKind | null;

export function DunningWorkflowDrawer({ workflowItemId, onClose }: Props) {
  const { i18n } = useLingui();
  const open = !!workflowItemId;
  const { data, isFetching, refetch } = useDunningWorkflowDetail(workflowItemId);
  const [activeModal, setActiveModal] = useState<ActiveModal>(null);
  // The blocker-banner Cancel button (awaiting_approval gate) opens a
  // dedicated modal so the operator can supply an optional reason. Distinct
  // from `activeModal === 'cancel'` which is the FSM-cancel modal driven by
  // the manual-actions section (and is destructive — terminates the FSM).
  const [approvalCancelOpen, setApprovalCancelOpen] = useState(false);

  const id = workflowItemId ?? '';
  const pause = usePauseDunning(id);
  const resume = useResumeDunning(id);
  const sendNow = useSendNowDunning(id);
  const handover = useHandoverDunning(id);
  const cancel = useCancelDunning(id);
  const retry = useRetryDunningSend(id);
  const navigate = useNavigate();

  const item = data?.item;
  const { currency: orgCurrency } = useCurrency();
  const currency = item?.currency || orgCurrency;
  const availableActions = data?.available_actions ?? [];
  const blocker = data?.blocker;
  const onOpenEmailCockpit = blocker?.email_log_id
    ? () => navigate(`/billing/email-cockpit?email_log_id=${blocker.email_log_id}`)
    : undefined;

  return (
    <>
      <Sheet open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
        <SheetContent
          side="right"
          size="lg"
          data-testid="dunning-workflow-drawer"
        >
          <SheetHeader>
            <SheetTitle className="sr-only">
              {item?.household_name || <Trans>Dunning workflow</Trans>}
            </SheetTitle>
            <SheetDescription className="sr-only">
              <Trans>Dunning workflow detail and manual actions.</Trans>
            </SheetDescription>
          </SheetHeader>

          <SheetBody>
            {isFetching && !data ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden="true" />
              </div>
            ) : data && item ? (
              <div className="space-y-6 mt-2">
                {/* §0 Blocker banner.
                    For `awaiting_approval` (email-approval gate), Send Now hits
                    POST /dunning-workflows/:id/send-now (the backend `SendNow`
                    handler approves the held email draft via
                    maybeDispatchApprovalGate). For other blocker reasons such as
                    `send_failed`, Send Now retries the workflow directly via
                    POST /dunning-workflows/:id/retry. The Cancel button is only
                    surfaced for the approval gate — for other blockers cancelling
                    is destructive and goes through the FSM via the drawer's
                    manual actions section. */}
                {blocker && (() => {
                  const isApprovalGate = blocker.reason === 'awaiting_approval';
                  const primaryPending = isApprovalGate ? sendNow.isPending : retry.isPending;
                  const handlePrimary = () => {
                    if (isApprovalGate) {
                      // Approve = endorse the system's plan. No reason needed
                      // (backend accepts an empty body / undefined request).
                      sendNow.mutate(undefined);
                    } else {
                      retry.mutate();
                    }
                  };
                  const handleCancelFromBanner = () => {
                    // Cancel routes through a reason modal so the operator can
                    // supply an optional explanation that is persisted on the
                    // discarded email log + the audit transition.
                    setApprovalCancelOpen(true);
                  };
                  return (
                    <DunningBlockerBanner
                      blocker={blocker}
                      onRetry={handlePrimary}
                      onOpenEmailCockpit={onOpenEmailCockpit}
                      onCancel={isApprovalGate ? handleCancelFromBanner : undefined}
                      retryPending={primaryPending}
                      cancelPending={isApprovalGate ? cancel.isPending : undefined}
                    />
                  );
                })()}

                {/* §1 Header */}
                <DunningWorkflowHeader item={item} />

                <Separator />

                {/* §2 Open invoices */}
                <section>
                  <h3 className="font-semibold mb-2">
                    <Trans>Open invoices</Trans>
                  </h3>
                  <DunningOpenInvoicesTable
                    invoices={data.open_invoices ?? []}
                    currency={currency}
                  />
                </section>

                <Separator />

                {/* §3 Timeline */}
                <section>
                  <h3 className="font-semibold mb-2">
                    <Trans>Timeline</Trans>
                  </h3>
                  <DunningWorkflowTimeline timeline={data.timeline ?? []} />
                </section>

                <Separator />

                {/* §4 Manual actions */}
                <section>
                  <h3 className="font-semibold mb-2">
                    <Trans>Manual actions</Trans>
                  </h3>
                  <DunningManualActions
                    item={item}
                    availableActions={availableActions}
                    onAction={(kind) => setActiveModal(kind)}
                  />
                </section>

                <Separator />

                {/* §5 Settings snapshot */}
                <section>
                  <h3 className="font-semibold mb-2">
                    <Trans>Settings snapshot</Trans>
                  </h3>
                  <DunningSettingsSnapshot snapshot={data.settings_snapshot} />
                </section>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center gap-3 p-8 text-center"
                data-testid="dunning-workflow-drawer-empty"
              >
                <AlertCircle className="h-8 w-8 text-muted-foreground" aria-hidden="true" />
                <p className="text-sm font-medium">
                  <Trans id="dunning.cockpit.drawer.empty.title">
                    Workflow not available
                  </Trans>
                </p>
                <p className="text-sm text-muted-foreground">
                  <Trans id="dunning.cockpit.drawer.empty.body">
                    We couldn&apos;t load this dunning workflow. It may have been
                    removed, or the request failed.
                  </Trans>
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetch()}
                  disabled={isFetching}
                >
                  {isFetching ? (
                    <Trans id="dunning.cockpit.drawer.empty.retrying">Retrying…</Trans>
                  ) : (
                    <Trans id="dunning.cockpit.drawer.empty.retry">Retry</Trans>
                  )}
                </Button>
              </div>
            )}
          </SheetBody>
        </SheetContent>
      </Sheet>

      <PauseModal
        open={activeModal === 'pause'}
        householdName={item?.household_name ?? ''}
        onClose={() => setActiveModal(null)}
        onConfirm={(req) => {
          pause.mutate(req, { onSuccess: () => setActiveModal(null) });
        }}
        loading={pause.isPending}
      />

      <ConfirmWithReasonModal
        open={activeModal === 'resume'}
        title={i18n._(msg({ id: 'dunning.action.resume.modal-title', message: 'Resume dunning' }))}
        explainer={i18n._(msg({ id: 'dunning.action.resume.modal-explainer', message: 'The workflow will resume from where it left off.' }))}
        confirmLabel={i18n._(msg({ id: 'dunning.action.resume.confirm-label', message: 'Resume' }))}
        confirmVariant="default"
        onClose={() => setActiveModal(null)}
        onConfirm={(reason) => resume.mutate({ reason }, { onSuccess: () => setActiveModal(null) })}
        loading={resume.isPending}
      />

      <ConfirmWithReasonModal
        open={activeModal === 'send-now'}
        title={i18n._(msg({ id: 'dunning.action.send-now.modal-title', message: 'Send next reminder now' }))}
        explainer={i18n._(msg({ id: 'dunning.action.send-now.modal-explainer', message: 'Bypass the schedule and queue the next reminder for delivery.' }))}
        confirmLabel={i18n._(msg({ id: 'dunning.action.send-now.confirm-label', message: 'Send now' }))}
        confirmVariant="default"
        onClose={() => setActiveModal(null)}
        onConfirm={(reason) => sendNow.mutate({ reason }, { onSuccess: () => setActiveModal(null) })}
        loading={sendNow.isPending}
      />

      <ConfirmWithReasonModal
        open={activeModal === 'handover'}
        title={i18n._(msg({ id: 'dunning.action.handover.modal-title', message: 'Mark Collections Handover' }))}
        explainer={i18n._(msg({ id: 'dunning.action.handover.modal-explainer', message: 'Move this workflow to collections handover. This is terminal.' }))}
        confirmLabel={i18n._(msg({ id: 'dunning.action.handover.confirm-label', message: 'Mark handover' }))}
        confirmVariant="default"
        onClose={() => setActiveModal(null)}
        onConfirm={(reason) => handover.mutate({ reason }, { onSuccess: () => setActiveModal(null) })}
        loading={handover.isPending}
      />

      <ConfirmWithReasonModal
        open={activeModal === 'cancel'}
        title={i18n._(msg({ id: 'dunning.action.cancel.modal-title', message: 'Cancel dunning' }))}
        explainer={i18n._(msg({ id: 'dunning.action.cancel.modal-explainer', message: 'Cancel this workflow permanently. This is terminal.' }))}
        confirmLabel={i18n._(msg({ id: 'dunning.action.cancel.confirm-label', message: 'Cancel workflow' }))}
        confirmVariant="destructive"
        onClose={() => setActiveModal(null)}
        onConfirm={(reason) => cancel.mutate({ reason }, { onSuccess: () => setActiveModal(null) })}
        loading={cancel.isPending}
      />

      {/* Approval-gate cancel modal — opens from the awaiting_approval blocker
          banner. Cancels this single queued email only (the next sweep
          re-evaluates the debtor per cadence). Reason is OPTIONAL: the
          backend's CancelDunningEmail accepts an empty string and the modal
          uses the optional-reason zod schema. */}
      <ConfirmWithReasonModal
        open={approvalCancelOpen}
        title={i18n._(msg({ id: 'dunning.actions.cancel.modal.title', message: 'Cancel this email' }))}
        explainer={i18n._(msg({ id: 'dunning.actions.cancel.modal.description', message: 'This cancels just this single email. The next sweep will re-evaluate the debtor per cadence.' }))}
        confirmLabel={i18n._(msg({ id: 'dunning.actions.cancel.modal.confirm', message: 'Cancel email' }))}
        dismissLabel={i18n._(msg({ id: 'dunning.actions.cancel.modal.dismiss', message: 'Keep email' }))}
        reasonLabel={i18n._(msg({ id: 'dunning.actions.cancel.modal.reasonLabel', message: 'Reason (optional)' }))}
        confirmVariant="destructive"
        optionalReason
        onClose={() => setApprovalCancelOpen(false)}
        onConfirm={(reason) =>
          cancel.mutate(
            { reason },
            { onSuccess: () => setApprovalCancelOpen(false) },
          )
        }
        loading={cancel.isPending}
      />
    </>
  );
}
